// src/views/dispatchBoard/component/job-list/JobList.js
import React, { useEffect, useState } from "react";
import {
  Box,
  List,
  ListItem,
  Typography,
  Collapse,
  LinearProgress,
  CircularProgress,
  Backdrop
} from "@mui/material";
import { useTheme } from "@mui/material";
import CustomChip from "components/mui/chip";
import {
  getServiceRequests,
  getWorkRequirements,
  getBudgetedSummaries,
} from "../../logic/DispatchBoardLogic";

import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import JobDetailDialog from "screens/WorkOrders/component/JobDetail";
import { useWorkRequirementLogic } from "screens/WorkOrders/logic/work-requirement-logic";
import { useDispatchBoardPermission } from "screens/DispatchBoard/permissions/DispatchBoardPermissions";
import { useDispatch } from "react-redux";
import { handleSelectEvent } from "store/apps/calendar";
import { GetCustomer } from "screens/Customers/logic/GetCustomer";
import { GetJobsite } from "screens/Customers/logic/GetJobsites";


const JobList = ({ 
  toast,
  filterType,
  refreshKey,
  onOpenAddEventSidebar,
  onSelectEvent,
  onJobCreated,
  selectedDate,
  calendarViewType,
  appliedFilters
}) => {
  //#region Permissions
  const { DispatchBoardPermissionProps } = useDispatchBoardPermission();
  const {
    hasDbGetPermission,
  } = DispatchBoardPermissionProps;
  //#endregion Permissions

  const theme = useTheme();
  
  // now an object keyed by requestID, not a single boolean
  const [openDetails, setOpenDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [, setWrLoading]   = useState(false)

  const [joinedData, setJoinedData] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [calendarApi, setCalendarApi] = useState(null);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  const [openJobDetail, setOpenJobDetail] = useState(false);
  const [selectedWorkReq, setSelectedWorkReq] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [rules, setRules] = useState(
    { 
      canEdit: true,
      canDelete: true,
      canSubmit: true,
      nextStatuses: [],
      cannotGoBack: false
    });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { WorkRequirementsProps } = useWorkRequirementLogic({
    requestID: null,           // we’ll override per‐WR below
    navigate,
    toast,
    setLoading: setWrLoading,
    serviceRequest: {}         // dummy, since we only need the fetch fn
  });

  const { fetchWorkRequirements } = WorkRequirementsProps;

  useEffect(() => {
    const fetchAndJoin = async () => {
      if(!hasDbGetPermission) return;

      let svcReqs = [];
      let svcReqsWOs = []
      try {
        svcReqs = await getServiceRequests(navigate);
        if (svcReqs){
          svcReqsWOs = svcReqs.filter((sr) => sr.entityType === "WorkOrder")
        }
      } catch {
        toast.error("Unable to load jobs — please check your login.", {
          position: "top-right",
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }

      let wrs = [];
      try {
        wrs = await getWorkRequirements(navigate);
      } catch {
        toast.error("Unable to load job details.", {
          position: "top-right",
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }

      let budgets = [];
      try {
        budgets = await getBudgetedSummaries(navigate);
        // console.log(budgets)
      } catch {
        toast.error("Unable to load cost/labor data.", {
          position: "top-right",
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }

      const joined = svcReqsWOs
      .map((sr) => {
        // find *all* WRs for this SR
        const matchingWRs = wrs.filter((w) => w.entityID === sr.requestID);
    
        // for each WR, keep only occNo>0 jobs and add industryName
        const filteredWRs = matchingWRs
          .map((wr) => {
            const jobs = (wr.jobs || []).filter((j) => j.occurrenceNo > 0);
            return jobs.length
              ? { 
                  ...wr, 
                  jobs,
                  industryName: wr.industryName || null // Add industryName
                }
              : null;
          })
          .filter(Boolean);
    
        if (filteredWRs.length === 0) return null;
    
        const budget = budgets.find((b) => b.id === sr.requestID) || {};
    
        return {
          ...sr,
          // now an *array* of WRs
          workRequirements: filteredWRs,
          budgetSummary: budget,
        };
      })
      .filter(Boolean);

      setJoinedData(joined);
      
    // — JOIN ASSESSMENTS —
    const assessmentSRs = svcReqs.filter((sr) => sr.entityType === "Assessment");
   
    const joinedAssess = assessmentSRs
      .map((sr) => {
        const wr = wrs.find(
          (w) =>
            w.entityID === sr.requestID &&
            w.subServiceID === 0 &&
            Array.isArray(w.jobs) &&
            w.jobs.length >= 1
        );
        
        if (!wr) return null;
        const j0 = wr.jobs.find((j) => j.occurrenceNo === 0);
        if (!j0 || !j0.scheduleStartDate) return null;
        
        // Combine date and time for scheduleStart
        let scheduleStart = null;
        if (j0.scheduleStartDate) {
          scheduleStart = j0.scheduleStartDate;
          if (j0.scheduleStartTime) {
            const dateStr = dayjs(j0.scheduleStartDate).format('YYYY-MM-DD');
            scheduleStart = `${dateStr}T${j0.scheduleStartTime}`;
          }
        }
        
        return {
          id: sr.requestID,
          title: sr.requestTitle,
          scheduleStart: scheduleStart,
          assignees: j0.assignees,
          status: sr.status || null,
          industryID: wr.industryID || null,
          industryName: wr.industryName || null,
          subServiceID: wr.subServiceID || null,
          subServiceName: wr.subServiceName || null,
          // format YYYY-MM-DD
          dueBy: dayjs(j0.scheduleStartDate).format("YYYY-MM-DD"),
        };
      })
      .filter(Boolean)
      // upcoming first
      .sort((a, b) => dayjs(a.dueBy) - dayjs(b.dueBy));
      
    setAssessments(joinedAssess);
    setLoading(false);
    
  };
    if(hasDbGetPermission){
      fetchAndJoin();
    }
  }, [filterType, refreshKey, selectedDate, calendarViewType, appliedFilters]);

  // toggles only the clicked work-order
  const handleToggleDetails = (id) => {
    setOpenDetails((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAssessmentClick = async (assessment) => {
    setLoadingAssessment(true);
    setSelectedAssessment(assessment);
    try {
      const allWRs = await getWorkRequirements(navigate);
      const workReq = allWRs.find(wr => wr.entityID === assessment.id && wr.subServiceID === 0);
      
      if (!workReq) {
        toast.error("Assessment details not found", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        return;
      }
      
      const job = workReq.jobs.find(j => j.occurrenceNo === 0);
      if (!job) {
        toast.error("Assessment job not found", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        return;
      }
  
      // Get the complete service request data for this assessment
      const allSRs = await getServiceRequests(navigate);
      const assessmentSR = allSRs.find(sr => sr.requestID === assessment.id);
      
      if (!assessmentSR) {
        toast.error("Assessment service request not found");
        return;
      }
  
      // Get customers and jobsites to find the right objects
      const customers = await GetCustomer(0, navigate);
      const jobsites = await GetJobsite(assessmentSR.customerID, 0, navigate);
      
      const customer = customers.find(c => c.customerID === assessmentSR.customerID);
      const jobsite = jobsites.find(j => j.jobsiteID === assessmentSR.jobSiteID);
  
      // Create the event object with ALL required fields
      // Combine date and time for start/end dates
      let startDateTime = null;
      let endDateTime = null;
      
      if (job.scheduleStartDate) {
        startDateTime = job.scheduleStartDate;
        if (job.scheduleStartTime) {
          const dateStr = dayjs(job.scheduleStartDate).format('YYYY-MM-DD');
          startDateTime = `${dateStr}T${job.scheduleStartTime}`;
        }
      }
      
      if (job.scheduleEndDate) {
        endDateTime = job.scheduleEndDate;
        if (job.scheduleEndTime) {
          const dateStr = dayjs(job.scheduleEndDate).format('YYYY-MM-DD');
          endDateTime = `${dateStr}T${job.scheduleEndTime}`;
        }
      }
      
      const assessmentEvent = {
        id: assessment.id.toString(),
        title: assessment.title,
        start: startDateTime ? new Date(startDateTime) : null,
        end: endDateTime ? new Date(endDateTime) : null,
        allDay: false,
        extendedProps: {
          // Identification fields
          calendar: "Assessment",
          requestID: assessment.id,
          workReqID: workReq.workReqID,
          jobID: job.jobID,
          occurrenceNo: job.occurrenceNo,
          
          // Customer/Jobsite fields (with IDs for lookup)
          customerID: assessmentSR.customerID,
          jobSiteID: assessmentSR.jobSiteID,
          subServiceID: 0,
          
          // Display fields
          customerName: assessmentSR.customerName,
          jobSiteName: assessmentSR.jobSiteAddress || assessmentSR.jobSiteName,
          subServiceName: "Assessment",
          
          // Form data fields
          assignees: job.assignees || [],
          description: workReq.description || assessmentSR.reqDescription || "",
          specialInstructions: workReq.specialInstructions || [],
          requestTitle: assessment.title,
          
          // Additional fields that might be needed
          customer: customer, // Pass the actual customer object
          jobsite: jobsite,   // Pass the actual jobsite object
          service: null,      // Assessments don't have services
          
          // These might be needed by the sidebar
          guests: job.assignees || [],
          location: assessmentSR.jobSiteAddress || ""
        }
      };
      
      // console.log("Dispatching assessment event:", assessmentEvent);
      dispatch(handleSelectEvent(assessmentEvent));
      
      // setTimeout(() => {
        if (onOpenAddEventSidebar) {
          onOpenAddEventSidebar();
        }
      // }, 100);
      
    } catch (error) {
      console.error("Error loading assessment details:", error);
      toast.error("Error loading assessment details");
    } finally {
      setLoadingAssessment(false);
      setSelectedAssessment(null);
    }
  };

  // your WorkOrder UI, now parameterized by sr and per-card open state
  const WorkOrder = ({ sr }) => {
    if(!hasDbGetPermission) return;

    const wrs = sr.workRequirements;            // array now
// collapse‐view needs *all* jobs across all WRs
    const allJobs = wrs.flatMap((wr) => wr.jobs);

    const isOpen = !!openDetails[sr.requestID];

    // Due By = max scheduleEnd (combine date and time)
    const dueByRaw = allJobs.reduce((max, j) => {
      if (!j.scheduleEndDate) return max;
      
      // Combine date and time for comparison
      let endDateTime = j.scheduleEndDate;
      if (j.scheduleEndTime) {
        const dateStr = dayjs(j.scheduleEndDate).format('YYYY-MM-DD');
        endDateTime = `${dateStr}T${j.scheduleEndTime}`;
      }
      
      return !max || endDateTime > max ? endDateTime : max;
    }, null);
    const dueBy = dueByRaw
     ? dayjs(dueByRaw).format("YYYY-MM-DD")
     : "N/A"

    // % Complete
    const totalJobs = allJobs.length;
    const completedJobs = allJobs.filter((j) => j.status === "Completed").length;
    const percentComplete = totalJobs
      ? Math.round((completedJobs / totalJobs) * 100)
      : 0;

    // Recurring count
    const recurringCount = wrs.reduce(
      (sum, wr) => sum + (wr.numberOfOccurrences || 0),
      0
    );

    const totalCount = allJobs.length;
console.log(sr)
    // Budget fields
    const estCost =
      sr.budgetSummary.totalPrice != null
        ? `$${sr.budgetSummary.totalPrice}`
        : "—";
    const estLabor =
      sr.budgetSummary.totalManHours != null
        ? `${sr.budgetSummary.totalManHours}h`
        : "—";

    return (
      <>
        <ListItem
          disablePadding
          onClick={() => handleToggleDetails(sr.requestID)}
          sx={{
            border: "1px solid #E1E4EA",
            borderRadius: 1,
            backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff",
            px: 4,
            py: isOpen ? 2 : 4,
            mb: 2,
            cursor: "pointer",
          }}
        >
          {isOpen ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography variant="h6" color="text.primary">
                List View
              </Typography>
              <CustomChip
                skin="light"
                color="success"
                label={totalCount}
                size="small"
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {sr.customerName}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      borderRadius: "4px",
                      backgroundColor: theme.palette.main.main,
                      padding: "2px 12px",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontSize="0.5rem"
                      color={theme.palette.main.contrastText}
                    >
                      WO-{sr.requestID}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption">Due By {dueBy}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">
                  Est. Project Cost {estCost}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {percentComplete}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percentComplete}
                sx={{ height: 7 }}
              />
            </Box>
          )}
        </ListItem>

        <Collapse in={isOpen} timeout="auto" sx={{ backgroundColor: "#F6F7FA", mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff",
              p: 2,
              gap: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#f4f8fb",
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" color="info.main" fontWeight="bold">
                  WO-{sr.requestID}
                </Typography>
                <Typography variant="h6" color="text.primary">
                  {sr.customerName}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {sr.jobSiteAddress || sr.jobSiteName}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", textAlign: "end" }}>
                <Typography variant="body2" fontWeight="bold">
                  Est. Project Cost {estCost}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  Est. Labor Hrs {estLabor}
                </Typography>
                <Typography variant="body2" color="info.main" fontWeight="bold">
                  Due By {dueBy}
                </Typography>
                <Typography variant="body2">
                  Total Completion {percentComplete}%
                </Typography>
                <Typography variant="body2">
                  Recurring Jobs {recurringCount}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 2,
              }}
            >
            {wrs.map((wr) => {
            // compute wr’s own completion rate
            const wrTotal = wr.jobs.length;
            const wrDone =
              wr.jobs.filter((j) => j.status === "Completed").length;
            const wrPct = wrTotal
              ? Math.round((wrDone / wrTotal) * 100)
              : 0;

            return (
              <Box key={wr.workReqID} sx={{ mb: 4 }}>
                {/* service header */}
                <Box sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#f4f8fb",
                  p: 2,
                }}>
                  <Typography variant="h6">{wr.subServiceName}</Typography>
                  <Typography variant="h6">{wrPct}%</Typography>
                </Box>

                {/* job entries */}
                <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 2,
                }}>
                  {wr.jobs.map((j) => {
                    const dateLabel = j.scheduleEndDate
                      ? dayjs(j.scheduleEndDate).format("YYYY-MM-DD")
                      : "N/A";
                    return (
                      <Box
                        key={j.jobID}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#F6F7FA",
                          p: 2,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedWorkReq(wr);       // the WR that j came from
                          setSelectedJob(j);            // the job you clicked
                          setOpenJobDetail(true);
                        }}
                      >
                      <Typography
                        variant="h6"
                        sx={{
                          cursor: "pointer",             // pointer on the text itself
                          "&:hover": {
                            textDecoration: "underline"  // underline on hover
                          }
                        }}
                      >
                        Job {j.occurrenceNo}
                      </Typography>
                        <CustomChip
                          skin="dark"
                          color="success"
                          label={dateLabel}
                          size="small"
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
            </Box>
          </Box>
        </Collapse>
      </>
    );
  };

   // render one assessment row
   const AssessmentItem = ({ assessment }) => (
    <ListItem
      disablePadding
      onClick={() => handleAssessmentClick(assessment)}
      sx={{
        border: "1px solid #E1E4EA",
        borderRadius: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff",
        px: 4,
        py: 4,
        mb: 4,
        cursor: "pointer",
      }}

    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body1" fontWeight="bold">
            {assessment.title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
              backgroundColor: theme.palette.info.main,
              padding: "2px 12px",
            }}
          >
            <Typography
              variant="caption"
              fontSize="0.5rem"
              color={theme.palette.main.contrastText}
              sx={{
                cursor: "pointer",             // pointer on the text itself
                "&:hover": {
                  textDecoration: "underline"  // underline on hover
                }
              }}
            >
              AST-{assessment.id}
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption">Due By {assessment.dueBy}</Typography>
      </Box>
    </ListItem>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredWorkOrders = joinedData
  .map((sr) => {
    // workRequirements is now an array
    const wrs = sr.workRequirements;

    // for each WR, filter its jobs according to filterType and appliedFilters
    const filteredWRs = wrs
      .map((wr) => {
        // Apply industry filter first (at WR level) - by name
        if (appliedFilters?.industries?.length > 0) {
          const industryMatch = appliedFilters.industries.some(
            industryName => wr.industryName === industryName
          );
          if (!industryMatch) return null;
        }

        // Apply service filter (at WR level) - by name
        if (appliedFilters?.services?.length > 0) {
          const serviceMatch = appliedFilters.services.some(
            serviceName => wr.subServiceName === serviceName
          );
          if (!serviceMatch) return null;
        }

        let jobs = wr.jobs;
        
        // Filter by date if selectedDate is provided - range depends on calendar view
        if (selectedDate) {
          jobs = jobs.filter((j) => {
            if (!j.scheduleStartDate) return false;
            
            const jobDate = dayjs(j.scheduleStartDate);
            const selectedDayjs = dayjs(selectedDate);
            
            // Determine date range based on calendar view type
            switch (calendarViewType) {
              case 'timeGridDay': // Day view
                return jobDate.isSame(selectedDayjs, 'day');
              
              case 'timeGridWeek': // Week view
                return jobDate.isSame(selectedDayjs, 'week');
              
              case 'dayGridMonth': // Month view
              case 'listMonth': // List view (treat as month)
              default:
                return jobDate.isSame(selectedDayjs, 'month');
            }
          });
        }
        
        // Filter by type
        if (filterType === "scheduled") {
          jobs = jobs.filter((j) => Boolean(j.scheduleStartDate));
        } else if (filterType === "Unscheduled") {
          jobs = jobs.filter((j) => !j.scheduleStartDate);
        } else if (filterType === "dispatched") {
          jobs = jobs.filter((j) => j.assignees?.length > 0);
        }

        // Apply status filter (at job level)
        if (appliedFilters?.statuses?.length > 0) {
          jobs = jobs.filter((j) => {
            const jobStatus = j.status || 'Unscheduled';
            return appliedFilters.statuses.some(
              status => status.toLowerCase() === jobStatus.toLowerCase()
            );
          });
        }

        // only keep WRs that still have jobs
        return jobs.length ? { ...wr, jobs } : null;
      })
      .filter(Boolean);

    // drop the entire SR if none of its WRs survived
    if (filteredWRs.length === 0) return null;

    // return SR with its filtered array of WRs
    return { ...sr, workRequirements: filteredWRs };
  })
  .filter(Boolean);

  const filteredAssessments = assessments.filter((a) => {
    // Apply industry filter - by name
    if (appliedFilters?.industries?.length > 0) {
      const industryMatch = appliedFilters.industries.some(
        industryName => a.industryName === industryName
      );
      if (!industryMatch) return false;
    }

    // Apply service filter - by name
    if (appliedFilters?.services?.length > 0) {
      const serviceMatch = appliedFilters.services.some(
        serviceName => a.subServiceName === serviceName
      );
      if (!serviceMatch) return false;
    }

    // Apply status filter
    if (appliedFilters?.statuses?.length > 0) {
      const assessmentStatus = a.status || 'Unscheduled';
      const statusMatch = appliedFilters.statuses.some(
        status => status.toLowerCase() === assessmentStatus.toLowerCase()
      );
      if (!statusMatch) return false;
    }

    // Filter by date if selectedDate is provided - range depends on calendar view
    if (selectedDate) {
      const assessmentDate = dayjs(a.dueBy);
      const selectedDayjs = dayjs(selectedDate);
      
      // Determine date range based on calendar view type
      let dateMatch = false;
      switch (calendarViewType) {
        case 'timeGridDay': // Day view
          dateMatch = assessmentDate.isSame(selectedDayjs, 'day');
          break;
        
        case 'timeGridWeek': // Week view
          dateMatch = assessmentDate.isSame(selectedDayjs, 'week');
          break;
        
        case 'dayGridMonth': // Month view
        case 'listMonth': // List view (treat as month)
        default:
          dateMatch = assessmentDate.isSame(selectedDayjs, 'month');
          break;
      }
      
      if (!dateMatch) return false;
    }
    
    // Filter by type
    switch (filterType) {
      case "scheduled":
        return Boolean(a.scheduleStart); // This uses the combined scheduleStart we created above
      case "Unscheduled":
        return !a.scheduleStart; // This uses the combined scheduleStart we created above
      case "dispatched":
        return a.assignees?.length > 0;
      default: // "all"
        return true;
    }
  });

  const merged = [
    // WorkOrders
    ...filteredWorkOrders.map((sr) => {
      // gather all jobs from every WR
      const allJobs = sr.workRequirements.flatMap((wr) => wr.jobs);

      // find the latest scheduleEnd (combine date and time)
      const dueByRaw = allJobs.reduce((max, j) => {
        if (!j.scheduleEndDate) return max;
        
        // Combine date and time for comparison
        let endDateTime = j.scheduleEndDate;
        if (j.scheduleEndTime) {
          const dateStr = dayjs(j.scheduleEndDate).format('YYYY-MM-DD');
          endDateTime = `${dateStr}T${j.scheduleEndTime}`;
        }
        
        return !max || endDateTime > max ? endDateTime : max;
      }, null);

      const dueBy = dueByRaw
        ? dayjs(dueByRaw).format("YYYY-MM-DD")
        : "9999-12-31";

      return { type: "WO", key: sr.requestID, payload: sr, dueBy };
    }),
    // Assessments
    ...filteredAssessments.map((a) => ({
      type: "AST",
      key: a.id,
      payload: a,
      dueBy: typeof a.dueBy === "string"
      ? a.dueBy
      : a.dueBy.format("YYYY-MM-DD"),
    })),
  ];

// sort ascending by real date
  merged.sort((a, b) => dayjs(a.dueBy) - dayjs(b.dueBy));

  return (
    <>
     <Box sx={{ 
    height: '92vh', 
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden' // Contains the list and prevents double scrollbars
  }}>
    <List 
      component="nav" 
      aria-label="main mailbox"
      sx={{
        
        overflowY: 'auto',
        paddingRight: '4px',
        marginRight: '8px',
        marginLeft: '16px',
        '&::-webkit-scrollbar': {
          width: '10px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.palette.grey[100],
          marginBlock: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.grey[400],
          borderRadius: '6px',
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: theme.palette.grey[500],
        }
      }}
    >
      {merged.map((item) =>
        item.type === "WO" ? (
          <WorkOrder key={item.key} sr={item.payload} />
        ) : (
          <AssessmentItem key={item.key} assessment={item.payload} />
        )
      )}
    </List>
  </Box>
    
      <JobDetailDialog
        open={openJobDetail}
        handleClose={() => setOpenJobDetail(false)}
        method="detail"
        entity="workOrder-detail"
        rules={rules}                         
        workReq={selectedWorkReq}
        job={selectedJob}
        setJob={setSelectedJob}
        fetchWorkRequirements={fetchWorkRequirements}  
        setLoading={setWrLoading}
        toast={toast}
        fetchRequests={()=>{}}
      />
      <Backdrop
          sx={{ 
            color: "#fff", 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
          open={loadingAssessment}
        >
          <CircularProgress color="inherit" />
          
        </Backdrop>
    </>
  );

  
};

export default JobList;
